
import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { useMountSuffix } from "@/hooks/useMountSuffix";

interface Notification {
  id: number;
  type: string;
  content: string;
  report_id: number;
  read: boolean;
  created_at: string;
}

const NotificationBell = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const channelRef = useRef<any>(null);
  const suffix = useMountSuffix();

  const { data: notifications = [], error } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      console.log("Fetching notifications...");
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) {
        console.log("No user session found");
        return [];
      }

      const { data, error } = await (supabase.from as any)('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching notifications:", error);
        throw error;
      }

      console.log("Notifications fetched successfully:", data);
      return data as Notification[];
    },
  });

  // Log any errors that occur during the fetch
  useEffect(() => {
    if (error) {
      console.error("Error in notifications query:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les notifications",
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    let isMounted = true;
    const channelName = `notifications-${suffix}`;
    console.log('[NotificationBell] setup — channel:', channelName);

    let channel: any = null;
    try {
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications'
          },
          (payload) => {
            if (!isMounted) return;
            try {
              console.log(`[${channelName}] new notification payload:`, payload);
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              toast({
                title: "Nouvelle notification",
                description: payload.new.content,
              });
            } catch (err) {
              console.error(`[${channelName}] error handling payload:`, err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`[${channelName}] subscription status:`, status);
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[${channelName}] realtime unavailable`);
          }
        });

      channelRef.current = channel;
    } catch (err) {
      console.error(`[${channelName}] failed to set up subscription:`, err);
      channelRef.current = null;
    }

    return () => {
      isMounted = false;
      console.log(`[${channelName}] cleanup start`);
      if (!channel) {
        console.log(`[${channelName}] no channel to clean up`);
        return;
      }
      try {
        try {
          if (typeof channel.off === 'function') {
            channel.off('postgres_changes');
          }
        } catch (e) {
          console.warn(`[${channelName}] error removing listeners:`, e);
        }
        try {
          if (channel.bindings) {
            channel.bindings = {};
          }
        } catch (e) {
          // noop
        }
        channel.unsubscribe();
        supabase.removeChannel(channel);
        console.log(`[${channelName}] cleanup complete`);
      } catch (err) {
        console.warn(`[${channelName}] error cleaning up:`, err);
      }
      channelRef.current = null;
    };
  }, [queryClient, toast]);

  const markAsRead = async (notificationId: number) => {
    console.log("Marking notification as read:", notificationId);
    const { error } = await (supabase.from as any)('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error("Error marking notification as read:", error);
      toast({
        title: "Erreur",
        description: "Impossible de marquer la notification comme lue",
        variant: "destructive",
      });
      return;
    }

    console.log("Notification marked as read successfully");
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-100px)] mt-4">
          <div className="space-y-4">
            {notifications.length === 0 ? (
              <p className="text-center text-gray-500">
                Aucune notification
              </p>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border ${
                    notification.read ? 'bg-gray-50' : 'bg-primary/5 border-primary/20'
                  }`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <p className="text-sm">{notification.content}</p>
                  <span className="text-xs text-gray-500">
                    {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationBell;
